import { useState } from "react";
import { History, FileDown, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { api, fmt, fdatetime } from "@/lib/api";
import { downloadWith } from "@/lib/portalApi";
import { Section } from "@/components/common";

function VersionView({ v, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="invoice-version-dialog">
        <DialogHeader><DialogTitle>{v.number} v{v.version}</DialogTitle>
          <DialogDescription><span className="font-semibold text-rose-700" data-testid="invoice-version-superseded">Superseded by v{v.superseded_by}</span> · {fdatetime(v.superseded_at)} by {v.superseded_by_user}{v.change_reason && ` · ${v.change_reason}`}</DialogDescription></DialogHeader>
        <div className="max-h-[50vh] overflow-auto"><table className="po-table"><thead><tr><th>Description</th><th className="po-num">Qty</th><th className="po-num">Line total</th></tr></thead>
          <tbody>{(v.line_items || []).map((l, i) => <tr key={i}><td>{l.description}</td><td className="po-num">{l.quantity}</td><td className="po-num">{fmt(l.line_total, v.currency)}</td></tr>)}</tbody></table></div>
        <p className="text-sm">Subtotal <span className="tabular-nums">{fmt(v.subtotal ?? v.total, v.currency)}</span> · Total incl. tax <b className="tabular-nums">{fmt(v.total, v.currency)}</b></p>
        <DialogFooter><Button variant="outline" data-testid="invoice-version-pdf" onClick={() => downloadWith(api, `/tenant/invoices/${v.invoice_id}/versions/${v.version}/pdf`, `${v.number}-v${v.version}.pdf`, true)}><FileDown className="mr-1 h-4 w-4" />PDF</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceVersions({ inv, versions }) {
  const [open, setOpen] = useState(null);
  if (!versions?.length) return null;
  return (
    <Section title={`Versions (current v${inv.version || 1})`} testId="invoice-versions">
      <ul className="divide-y divide-slate-100">{versions.map((v) => (
        <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm" data-testid={`invoice-version-${v.version}`}>
          <span className="min-w-0"><b>v{v.version}</b> · {fmt(v.total, v.currency)}<span className="block text-xs text-slate-500">Superseded by v{v.superseded_by} · {fdatetime(v.superseded_at)}{v.via && v.via !== "edit" ? ` · from ${v.via}` : ""}</span></span>
          <Button size="sm" variant="outline" data-testid={`invoice-version-view-${v.version}`} onClick={() => setOpen(v)}><History className="mr-1 h-3.5 w-3.5" />View</Button>
        </li>))}</ul>
      {open && <VersionView v={open} onClose={() => setOpen(null)} />}
    </Section>
  );
}

export function UpdateFromQuoteButton({ inv, quote, onDone }) {
  const [qty, setQty] = useState(null);
  const [reason, setReason] = useState("");
  const save = async () => {
    const r = await act(() => api.post(`/tenant/invoices/${inv.id}/from-quote`, { quantities: qty.map(Number), reason }), `${inv.number} updated to a new version`);
    if (r) { setQty(null); setReason(""); onDone(); }
  };
  return (
    <>
      <Button variant="outline" data-testid="invoice-from-quote-button" onClick={() => setQty(quote.lines.map((l) => String(l.quantity)))}><RefreshCw className="mr-1 h-4 w-4" />Update from quote</Button>
      <Dialog open={!!qty} onOpenChange={(o) => !o && setQty(null)}>
        <DialogContent data-testid="invoice-from-quote-dialog">
          <DialogHeader><DialogTitle>Update {inv.number} from {quote.number} v{quote.version}</DialogTitle>
            <DialogDescription>Change the copies or quantity. The quotation gets a new version and this invoice becomes v{(inv.version || 1) + 1} on the same number. Linked jobs are not changed.</DialogDescription></DialogHeader>
          {qty && <div className="space-y-2">{quote.lines.map((l, i) => (
            <label key={i} className="flex items-center justify-between gap-3 text-sm"><span className="min-w-0 flex-1">{l.description}</span>
              <Input type="number" min="1" className="w-28" data-testid={`from-quote-qty-${i}`} value={qty[i]} onChange={(e) => setQty(qty.map((x, k) => (k === i ? e.target.value : x)))} /></label>))}
            <Textarea data-testid="from-quote-reason" placeholder="Reason for the change (required)" value={reason} onChange={(e) => setReason(e.target.value)} /></div>}
          <DialogFooter><Button data-testid="from-quote-confirm" disabled={reason.trim().length < 3 || !qty?.every((x) => Number(x) > 0)} onClick={save} className="bg-[#13294B]">Create new version</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
